import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Phone, Clock } from "lucide-react";
import EnquiryForm from "../components/EnquiryForm";
import { SERVICES, AREAS, CONTACT } from "../lib/constants";

const MARQUEE_WORDS = [
  "Commercial", "Residential", "Industrial", "Weekend Villa", "Plotting",
  "Prahlad Nagar", "Satellite", "Bodakdev", "Sindhu Bhavan", "Shela",
  "South Bopal", "Thaltej", "Science City", "Iscon Ambli",
];

const HomePage = () => {
  return (
    <div>
      <section className="relative overflow-hidden ve-noise">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(900px 520px at 85% 20%, rgba(193,68,14,0.18), transparent 60%), radial-gradient(700px 420px at 10% 90%, rgba(212,165,116,0.10), transparent 60%)",
          }}
        />
        <div className="ve-container relative pt-20 md:pt-28 pb-20 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <span className="ve-eyebrow ve-rise">Est. Ahmedabad · Real Estate Consulting</span>
              <h1
                className="ve-display mt-6 text-[2.8rem] sm:text-[3.6rem] md:text-[4.6rem] lg:text-[5.4rem] ve-rise ve-delay-1"
              >
                A quieter way to<br />
                <em>buy, sell &amp; invest</em><br />
                in Ahmedabad West.
              </h1>
              <p
                className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed ve-rise ve-delay-2"
                style={{ color: "var(--cream-200)" }}
              >
                Vachhraj Estate is a consulting-first real estate practice run out of Prahlad Nagar. We work across <strong style={{ color: "var(--gold-400)" }}>Commercial, Residential, Industrial, Weekend Villas</strong> and <strong style={{ color: "var(--gold-400)" }}>Plotting</strong> — with a focused footprint across Satellite, Bodakdev, Sindhu Bhavan, Shela, South Bopal, Thaltej, Science City, VIP Road, Vastrapur, Iscon Ambli and the SG Highway corridor. One conversation is usually enough to know if we're the right fit.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 ve-rise ve-delay-3">
                <a href={CONTACT.whatsappLink} target="_blank" rel="noreferrer" className="ve-btn-primary">
                  Start on WhatsApp
                </a>
                <a href={`tel:${CONTACT.phoneTel}`} className="ve-btn-ghost">
                  <Phone size={14} /> {CONTACT.phoneDisplay}
                </a>
                <Link to="/contact" className="ve-btn-ghost">
                  Visit the Office <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-4 ve-rise ve-delay-4">
              <div className="ve-card" style={{ borderColor: "var(--hair-strong)" }}>
                <div className="ve-number">01 / Office</div>
                <div className="mt-2 text-sm leading-relaxed" style={{ color: "var(--cream-200)" }}>
                  <MapPin size={14} className="inline mr-2" style={{ color: "var(--gold-500)" }} />
                  {CONTACT.addressLine2}, {CONTACT.addressLine4}
                </div>
                <div className="ve-rule my-6" />
                <div className="ve-number">02 / Hours</div>
                <div className="mt-2 text-sm" style={{ color: "var(--cream-200)" }}>
                  <Clock size={14} className="inline mr-2" style={{ color: "var(--gold-500)" }} />
                  Mon – Sat · 9:30 AM – 8:30 PM
                </div>
                <div className="ve-rule my-6" />
                <div className="ve-number">03 / Direct Line</div>
                <a
                  href={`tel:${CONTACT.phoneTel}`}
                  className="mt-2 block text-lg ve-link-underline"
                  style={{ fontFamily: "Fraunces, serif", color: "var(--cream-100)" }}
                >
                  {CONTACT.phoneDisplay}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div
          className="ve-rule"
          style={{
            borderTop: "1px solid var(--hair)",
            background: "var(--ink-900)",
          }}
        >
          <div className="ve-marquee">
            <div className="ve-marquee-track">
              {[...MARQUEE_WORDS, ...MARQUEE_WORDS, ...MARQUEE_WORDS, ...MARQUEE_WORDS].map((w, i) => (
                <span
                  key={i}
                  className="text-sm tracking-[0.22em] uppercase inline-flex items-center gap-8"
                  style={{ color: "var(--cream-300)" }}
                >
                  {w}
                  <span style={{ color: "var(--gold-500)" }}>✦</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ve-container py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="ve-eyebrow ve-slide-up">Our Promise</span>
            <h2 className="ve-display mt-5 text-4xl md:text-5xl ve-slide-up" style={{ animationDelay: '100ms' }}>
              Clarity. Discretion.<br />
              <em>Local intelligence.</em>
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8 ve-stagger">
            <div className="ve-card ve-slide-up ve-hover-lift">
              <div className="ve-number">01</div>
              <h3 className="mt-3 text-2xl">One point of contact</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--cream-200)" }}>
                You talk to the proprietor directly — no call-center, no handovers. Decisions move faster when fewer hands touch the file.
              </p>
            </div>
            <div className="ve-card ve-slide-up ve-hover-lift">
              <div className="ve-number">02</div>
              <h3 className="mt-3 text-2xl">Only what fits</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--cream-200)" }}>
                We show you three to five options that fit your budget, timeline and end-use — not a catalog of everything on the market. Saves weeks.
              </p>
            </div>
            <div className="ve-card ve-slide-up ve-hover-lift">
              <div className="ve-number">03</div>
              <h3 className="mt-3 text-2xl">Titles, always checked</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--cream-200)" }}>
                Every property we recommend has been pre-verified on title, approvals, occupancy and seller credibility. You pay for peace of mind.
              </p>
            </div>
            <div className="ve-card ve-slide-up ve-hover-lift">
              <div className="ve-number">04</div>
              <h3 className="mt-3 text-2xl">West Ahmedabad, deeply</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--cream-200)" }}>
                Our intelligence ends where you stop needing us — a tightly defined corridor from Vastrapur to Shela means we know every lane, every tower.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative border-t" style={{ borderColor: "var(--hair)", background: "var(--ink-900)" }}>
        <div className="ve-container py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="ve-eyebrow">What We Deal In</span>
              <h2 className="ve-display mt-5 text-4xl md:text-5xl">
                Five mandates.<br />
                <em>One Ahmedabad.</em>
              </h2>
            </div>
            <Link to="/services" className="ve-link-underline text-sm tracking-[0.2em] uppercase">
              View All Services <ArrowUpRight size={14} className="inline ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px ve-stagger" style={{ background: "var(--hair)" }}>
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services#${s.slug}`}
                className="p-8 md:p-10 group transition-all duration-300 ve-slide-up ve-hover-lift"
                style={{ background: "var(--ink-900)" }}
              >
                <div className="ve-number">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-4 text-3xl md:text-[2.2rem]" style={{ color: "var(--cream-100)" }}>
                  {s.title}
                </h3>
                <p className="mt-3 text-sm tracking-wide" style={{ color: "var(--gold-500)" }}>
                  {s.short}
                </p>
                <p className="mt-6 text-sm leading-relaxed" style={{ color: "var(--cream-200)" }}>
                  {s.body}
                </p>
                <div className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase group-hover:text-[color:var(--gold-400)] transition-colors" style={{ color: "var(--cream-300)" }}>
                  Read More <ArrowUpRight size={14} />
                </div>
              </Link>
            ))}
            <div className="p-8 md:p-10 flex flex-col justify-between" style={{ background: "var(--ink-900)" }}>
              <div>
                <div className="ve-number">06</div>
                <h3 className="mt-4 text-3xl md:text-[2.2rem]" style={{ color: "var(--cream-100)" }}>
                  Something else?
                </h3>
                <p className="mt-6 text-sm leading-relaxed" style={{ color: "var(--cream-200)" }}>
                  Pre-leased investments, land aggregation, joint-development advisory — if it's real estate in West Ahmedabad, we have probably done it before.
                </p>
              </div>
              <a href={CONTACT.whatsappLink} target="_blank" rel="noreferrer" className="ve-btn-primary mt-8 self-start">
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="ve-container py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="ve-eyebrow">Where We Work</span>
            <h2 className="ve-display mt-5 text-4xl md:text-5xl">
              A tightly held<br />
              <em>corner of Ahmedabad.</em>
            </h2>
            <p className="mt-6 text-sm leading-relaxed max-w-md" style={{ color: "var(--cream-200)" }}>
              We deliberately limit ourselves to Ahmedabad West. The payoff: street-level knowledge of pricing, builder reputations, resale depth and upcoming infrastructure in every micro-market we cover.
            </p>
            <Link to="/areas" className="inline-block mt-8 ve-btn-ghost">
              Explore All Areas <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-2 md:grid-cols-3 gap-px ve-stagger" style={{ background: "var(--hair)" }}>
              {AREAS.slice(0, 9).map((a, i) => (
                <li key={a.name} className="p-5 ve-slide-up ve-hover-lift" style={{ background: "var(--ink-800)" }}>
                  <div className="ve-number text-[0.78rem]">{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-2 text-xl" style={{ fontFamily: "Fraunces, serif", color: "var(--cream-100)" }}>
                    {a.name}
                  </div>
                  <div className="mt-1 text-[0.7rem] tracking-[0.18em] uppercase" style={{ color: "var(--gold-500)" }}>
                    {a.tag}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="relative border-t border-b" style={{ borderColor: "var(--hair)", background: "var(--ink-900)" }}>
        <div className="ve-container py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <span className="ve-eyebrow">Say hello</span>
            <h2 className="ve-display mt-5 text-4xl md:text-5xl">
              Tell us what you're<br />
              <em>looking for.</em>
            </h2>
            <p className="mt-6 text-sm leading-relaxed max-w-md" style={{ color: "var(--cream-200)" }}>
              Share a few details — you will hear back the same day, usually within a couple of hours. If it's urgent, WhatsApp is the fastest way to reach us.
            </p>
            <div className="mt-10 space-y-5 text-sm" style={{ color: "var(--cream-200)" }}>
              <div className="flex items-start gap-4">
                <MapPin size={16} style={{ color: "var(--gold-500)", marginTop: 3 }} />
                <div>
                  {CONTACT.addressLine1}<br />
                  {CONTACT.addressLine2}<br />
                  {CONTACT.addressLine4}, Ahmedabad 380015
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={16} style={{ color: "var(--gold-500)", marginTop: 3 }} />
                <a href={`tel:${CONTACT.phoneTel}`} className="ve-link-underline">
                  {CONTACT.phoneDisplay}
                </a>
              </div>
              <div className="flex items-start gap-4">
                <Clock size={16} style={{ color: "var(--gold-500)", marginTop: 3 }} />
                <div>Monday – Saturday · 9:30 AM – 8:30 PM</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <EnquiryForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;